import { useState, useEffect } from 'react';
import useSnackbar from './useSnackbar';
import { createProductPagination, createCrawlRequest } from '../types/product';
import { apiClient, useAPIErrorHandling } from '../utils/apiErrorHandling';

/**
 * 위버스 URL에서 아티스트 정보를 추출하는 함수
 * @param {string} url - 위버스 상품 URL
 * @returns {Object|null} 아티스트 정보 객체 또는 null
 */
const extractArtistFromWeverseUrl = (url) => {
    try {
        // 위버스 URL 패턴 예시: https://weverseshop.io/ko/shop/artist/ARTIST_ID/product/PRODUCT_ID
        const urlPattern = /\/artist\/([^/]+)\//;
        const match = url.match(urlPattern);
        
        if (match && match[1]) {
            const artistId = match[1];
            // 아티스트 ID를 기반으로 아티스트명 추출 (일반적인 패턴)
            // 실제로는 더 정교한 매핑이 필요할 수 있습니다
            const artistName = artistId.toUpperCase(); // 기본적으로 ID를 대문자로 변환
            
            return {
                name: artistName,
                id: artistId
            };
        }
        
        return null;
    } catch (error) {
        console.error('위버스 URL에서 아티스트 정보 추출 중 오류:', error);
        return null;
    }
};

const useProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(createProductPagination());
    const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
    const { handleAPISuccess } = useAPIErrorHandling(showSnackbar);

    const fetchProducts = async (page = 1, filters = {}) => {
        setLoading(true);
        setError(null);
        
        const {
            sourceSite = '',
            status = '',
            published_to_shopify = '',
            sortBy = 'crawled_at',
            sortOrder = 'desc'
        } = filters;
        
        const params = {
            page: page,
            size: 10,
            sort_by: sortBy,
            sort_order: sortOrder
        };
        
        if (sourceSite) {
            params.source_site = sourceSite;
        }
        
        if (status) {
            params.status = status;
        }
        
        if (published_to_shopify !== '') {
            params.published_to_shopify = published_to_shopify;
        }
        
        try {
            const result = await apiClient.get('/products/search', { params });
            console.log('Products API 응답:', result.data);
            
            // 응답 데이터 확인
            if (result.data && result.data.items) {
                setProducts(result.data.items);
                setPagination({
                    ...createProductPagination(),
                    total: result.data.total || 0,
                    page: result.data.page || 1,
                    size: result.data.size || 10,
                    pages: result.data.pages || 0,
                    has_next: result.data.has_next || false,
                    has_prev: result.data.has_prev || false
                });
            } else if (result.data && result.data.data && result.data.data.items) {
                // 중첩된 구조인 경우
                setProducts(result.data.data.items);
                setPagination({
                    ...createProductPagination(),
                    total: result.data.data.total || 0,
                    page: result.data.data.page || 1,
                    size: result.data.data.size || 10,
                    pages: result.data.data.pages || 0,
                    has_next: result.data.data.has_next || false,
                    has_prev: result.data.data.has_prev || false
                });
            } else {
                console.error('예상하지 못한 응답 구조:', result.data);
                setError('제품 데이터를 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('제품 불러오기 에러:', error);
            setError(error.response?.data?.message || '제품을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const addProduct = async (site, url) => {
        console.log(`${site} 단건 추가 - URL: ${url}`);
        
        const siteMapping = {
            '올리브영': 'oliveyoung',
            '무신사': 'musinsa',
            '위버스': 'weverse',
            '다이소': 'daiso',
            '국립박물관': 'museum',
            '케이타운': 'ktown'
        };
        
        const siteCode = siteMapping[site] || site.toLowerCase();
        
        const crawlRequest = createCrawlRequest(siteCode);
        crawlRequest.url = url;
        
        // 위버스 사이트인 경우 URL에서 아티스트 정보 추출
        if (siteCode === 'weverse' && url) {
            const artistInfo = extractArtistFromWeverseUrl(url);
            if (artistInfo) {
                crawlRequest.artist = artistInfo;
            }
        }
        
        try {
            await apiClient.post('/crawler/crawl', crawlRequest, { timeout: 300000 }); // 5분 타임아웃
            handleAPISuccess('제품이 성공적으로 추가되었습니다!');
            await fetchProducts(1, {});
            return { success: true };
        } catch (error) {
            console.error('제품 추가 에러:', error);
            showSnackbar(error.response?.data?.message || '제품 추가에 실패했습니다.', 'error');
            return { success: false, error };
        }
    };

    const deleteProducts = async (ids) => {
        console.log('삭제할 제품 IDs:', Array.from(ids));
        
        try {
            const result = await apiClient.delete('/products/bulk', { data: Array.from(ids) });
            handleAPISuccess(result.data?.message || '제품이 성공적으로 삭제되었습니다.');
            await fetchProducts(pagination.page, {});
            return { success: true };
        } catch (error) {
            console.error('제품 삭제 에러:', error);
            showSnackbar(error.response?.data?.message || '제품 삭제에 실패했습니다.', 'error');
            return { success: false, error };
        }
    };

    useEffect(() => {
        fetchProducts(1, {});
    }, []);

    return {
        products,
        loading,
        error,
        pagination,
        fetchProducts,
        addProduct,
        deleteProducts,
        snackbar,
        showSnackbar,
        closeSnackbar
    };
};

export default useProducts;
